import { Component, ChangeDetectionStrategy, OnDestroy } from '@angular/core'
import { CollectionFacade, isExperienceStatX } from '../../collection.facade'
import { map, takeUntil, withLatestFrom, distinctUntilChanged, take } from 'rxjs/operators'
import { combineLatest, Subject } from 'rxjs'
import { NodeService } from '../../../shared/services/node.service'
import { INode } from '../../../shared/models/node.model'
import { ApiNodeTypes, NonApiNodeTypes } from 'src/io/models/collection'
import { capitalizeFirstChar } from '../../../shared/utility-functions/string.utility'
import { buildCollectionTabPageCategory, buildCollectionTabPageContentTags } from './collection-tab-view.utility'
import { CollectionAnalyticsService } from '../../collection-analytics.service'

/**
 * Handle application level interactions (routing, metadata, etc) for a single
 * collection's child nodes view and translate data from collection facade to
 * child component props.
 * @deprecated
 * New bff-powered entity collections/hub initiative will replace this component and the collection facade.
 * See @EntityHubTabComponent
 */
@Component({
  selector: 'flo-collection-tab-view',
  templateUrl: './collection-tab-view.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CollectionTabViewComponent implements OnDestroy {
  constructor(
    private readonly collectionsFacade: CollectionFacade,
    private readonly nodeService: NodeService,
    private readonly collectionAnalyticsService: CollectionAnalyticsService
  ) {}

  private readonly ngOnDestroy$ = new Subject<void>()

  /**
   * pass actions from child components to CollectionFacade
   */
  public readonly action$ = this.collectionsFacade.action$

  public readonly nodeListComponentProps$ = combineLatest([
    this.collectionsFacade.nodes$,
    this.collectionsFacade.currentNodesParams$,
    this.collectionsFacade.selectedNode$,
    this.collectionsFacade.personResultsTab$
  ]).pipe(
    map(([nodes, params, selectedNode, results]) => ({
      nodes,
      nodesRequestParams: {
        ...params,
        // Tab content for grappling results is not visible without this
        type:
          results && params.type === ApiNodeTypes.result
            ? isExperienceStatX(results)
              ? NonApiNodeTypes.experienceGrappling
              : NonApiNodeTypes.grappling
            : params.type
      },
      selectedNode,
      results
    }))
  )

  // tslint:disable-next-line:no-unused-variable
  private updateSeo$ = combineLatest([this.collectionsFacade.currentNodesParams$, this.collectionsFacade.selectedNode$])
    .pipe(
      map(([params, selectedNode]) => selectedNode),
      withLatestFrom(this.collectionsFacade.collection$),
      map(([selectedNode, collection]) =>
        !!selectedNode
          ? selectedNode
          : {
              title: collection.meta_collections_page_title || collection.title,
              seo_description:
                collection.seo_description ||
                collection.description_plain ||
                collection.meta_collections_page_description,
              asset: { url: collection.asset_url }
            }
      ),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe(collection => this.nodeService.applyNodeMetaToPage(collection as INode))

  // tslint:disable-next-line:no-unused-variable
  private trackSegment = combineLatest([
    this.collectionsFacade.currentNodesParams$,
    this.collectionsFacade.selectedNode$,
    this.collectionsFacade.collection$
  ])
    .pipe(
      distinctUntilChanged(
        ([_currentNodesParams, _selectedNode, collection], [_currentNodesParams2, _selectedNode2, collection2]) =>
          collection.id === collection2.id
      ),
      map(([requestParam, selectedNode, collection]) => {
        const currentNode = !!selectedNode ? selectedNode : collection
        const videoSelected: boolean =
          !!selectedNode && selectedNode.id !== collection.id && (selectedNode.type as unknown as string) === 'video'
        return {
          ...currentNode,
          page_category: buildCollectionTabPageCategory(!!selectedNode, collection?.original_entity?.type),
          subpage_category: capitalizeFirstChar(requestParam.type),
          type: !!selectedNode ? selectedNode.type : 'Collection',
          contentTagProperties: buildCollectionTabPageContentTags(videoSelected, (currentNode as INode).node?.tags)
        }
      }),
      take(1)
    )
    .subscribe(res => {
      const { page_category, subpage_category, contentTagProperties, id } = res
      this.collectionAnalyticsService.sendPageCall(
        {
          category: 'Collection',
          page_category,
          subpage_category,
          autoPageCall: false,
          node_type: res.type?.toString()
        },
        {
          _tag: 'collection',
          node_id: id ?? undefined,
          content_tags: contentTagProperties
        }
      )
    })

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next()
    this.ngOnDestroy$.complete()
  }
}
